import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
} from "../../../components/ui/dialog";
import Button from "../../../components/button-v1";
import styles from "./styles.module.css";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import {
  updateMonitoringSession,
  getPatientMonitoringSessions,
} from "../../../services/patient/details";
import { getPatientLocationId } from "../../../utils/helper";
import { useSearchParams } from "react-router-dom";

const EditSession = ({ isEditDialogOpen, setIsEditDialogOpen, sessionId }) => {
  const [queryParams] = useSearchParams();
  const queryClient = useQueryClient();
  const patientId = sessionStorage.getItem("_pt_id");
  // Read/update the session under the PATIENT's own location (captured by the
  // parent Monitoring Time page), not the viewer's current location (SCRUM-157).
  const locationId = getPatientLocationId();
  const [hours, setHours] = useState("00");
  const [minutes, setMinutes] = useState("00");
  const [seconds, setSeconds] = useState("00");
  const [CCMMinutes, setCCMMinutes] = useState("00");
  const [CCMSeconds, setCCMSeconds] = useState("00");
  const [CCMHours, setCCMHours] = useState("00");
  const [monitoringSession, setMonitoringSession] = useState(null);

  const CCM = Boolean(Number.parseInt(queryParams.get("CCM")));
  const RPM = Boolean(Number.parseInt(queryParams.get("RPM")));

  const monitoringSessionQuery = useQuery(
    ["patient-monitoring-sessions", patientId, locationId],
    () => getPatientMonitoringSessions(patientId, locationId),
    {
      enabled: !!patientId && !!locationId,
    }
  );

  const monitoringSessions = monitoringSessionQuery?.data?.results ?? [];

  const { mutate: updateSession, isLoading: isUpdating } = useMutation(
    (data) => updateMonitoringSession(sessionId, data, locationId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          "patient-monitoring-sessions",
          patientId,
        ]);
        toast.success("Session updated successfully");
        setIsEditDialogOpen(false);
      },
      onError: () => {
        toast.error("Could not update session");
      },
    }
  );

  const handleChange = (e, timeType, type) => {
    if (type === "CCM") {
      if (timeType === "hours") {
        setCCMHours(e.target.value);
      }
      if (timeType === "minutes") {
        setCCMMinutes(e.target.value);
      }
      if (timeType === "seconds") {
        setCCMSeconds(e.target.value);
      }
    } else {
      if (timeType === "hours") {
        setHours(e.target.value);
      }
      if (timeType === "minutes") {
        setMinutes(e.target.value);
      }
      if (timeType === "seconds") {
        setSeconds(e.target.value);
      }
    }
  };

  const handleMinutesChange = (e) => {
    setMinutes(e.target.value);
  };

  const handleSecondsChange = (e) => {
    setSeconds(e.target.value);
  };

  const totalSeconds = (hours, minutes, seconds) => {
    const hoursInSeconds = parseInt(hours) * 3600;
    const minutesInSeconds = parseInt(minutes) * 60;
    const secondsValue = parseInt(seconds);

    return hoursInSeconds + minutesInSeconds + secondsValue;
  };

  const handleEditSave = () => {
    const totalRPMSeconds = totalSeconds(hours, minutes, seconds);
    const totalCCMSeconds = totalSeconds(CCMHours, CCMMinutes, CCMSeconds);

    if (totalRPMSeconds < 0 || totalCCMSeconds < 0) {
      toast.error("Time values cannot be negative");
      return;
    }

    if (totalRPMSeconds > 0 && totalCCMSeconds > 0) {
      updateSession({
        total_ccm_seconds: totalCCMSeconds,
        total_rpm_seconds: totalRPMSeconds,
      });
    } else if (totalRPMSeconds > 0) {
      updateSession({
        total_rpm_seconds: totalRPMSeconds,
      });
    } else if (totalCCMSeconds > 0) {
      updateSession({
        total_ccm_seconds: totalCCMSeconds,
      });
    } else {
      return;
    }
  };

  const setMonitoringEditTime = (totalSeconds, type) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const paddedHours = hours.toString().padStart(2, "0");
    const paddedMinutes = minutes.toString().padStart(2, "0");
    const paddedSeconds = seconds.toString().padStart(2, "0");
    if (type === "CCM") {
      setCCMHours(paddedHours);
      setCCMMinutes(paddedMinutes);
      setCCMSeconds(paddedSeconds);
    } else {
      setSeconds(paddedSeconds);
      setMinutes(paddedMinutes);
      setHours(paddedHours);
    }
  };

  useEffect(() => {
    if (monitoringSession) {
      if (monitoringSession.total_ccm_seconds > 0) {
        setMonitoringEditTime(monitoringSession.total_ccm_seconds, "CCM");
      } else {
        setMonitoringEditTime(monitoringSession.total_rpm_seconds, "RPM");
      }
    }
  }, [monitoringSession]);

  useEffect(() => {
    if (monitoringSessionQuery.isSuccess && monitoringSessions) {
      const session = monitoringSessions.find(
        (session) => session.id === sessionId
      );
      if (session) {
        setMonitoringSession(session);
      }
    }
  }, [monitoringSessionQuery.isSuccess, monitoringSessions, sessionId]);

  // Add a separate useEffect to handle sessionId changes directly
  useEffect(() => {
    if (sessionId && monitoringSessions) {
      const session = monitoringSessions.find(
        (session) => session.id === sessionId
      );
      if (session) {
        setMonitoringEditTime(session.total_rpm_seconds);
      }
    }
  }, [sessionId, monitoringSessions]);

  return (
    <Dialog open={isEditDialogOpen} setOpen={setIsEditDialogOpen}>
      <DialogContent>
        <DialogHeader>Edit Session</DialogHeader>
        <DialogBody>
          <div>
            {monitoringSession?.total_rpm_seconds > 0 && (
              <>
                <p className={styles.form_title}> RPM </p>
                <div className={styles.timeInputsContainer}>
                  <div className={styles.timeInput}>
                    <label className="label">Hours</label>

                    <input
                      type="number"
                      className="input_field no-arrows"
                      value={hours}
                      onChange={(e) => handleChange(e, "hours", "RPM")}
                      min={0}
                    />
                  </div>
                  <div className={styles.timeInput}>
                    <label className="label">Minutes</label>
                    <input
                      type="number"
                      className="input_field no-arrows"
                      value={minutes}
                      onChange={(e) => handleChange(e, "minutes", "RPM")}
                      min={0}
                    />
                  </div>
                  <div className={styles.timeInput}>
                    <label className="label">Seconds</label>
                    <input
                      type="number"
                      className="input_field no-arrows"
                      value={seconds}
                      onChange={(e) => handleChange(e, "seconds", "RPM")}
                      min={0}
                    />
                  </div>
                </div>
              </>
            )}

            {monitoringSession?.total_ccm_seconds > 0 && (
              <>
                <p className={styles.form_title}> CCM </p>
                <div className={styles.timeInputsContainer}>
                  <div className={styles.timeInput}>
                    <label className="label">Hours</label>

                    <input
                      type="number"
                      className="input_field no-arrows"
                      value={CCMHours}
                      onChange={(e) => handleChange(e, "hours", "CCM")}
                      min={0}
                    />
                  </div>
                  <div className={styles.timeInput}>
                    <label className="label">Minutes</label>
                    <input
                      type="number"
                      className="input_field no-arrows"
                      value={CCMMinutes}
                      onChange={(e) => handleChange(e, "minutes", "CCM")}
                      min={0}
                    />
                  </div>
                  <div className={styles.timeInput}>
                    <label className="label">Seconds</label>
                    <input
                      type="number"
                      className="input_field noa-arrows"
                      value={CCMSeconds}
                      onChange={(e) => handleChange(e, "seconds", "CCM")}
                      min={0}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        </DialogBody>
        <DialogFooter>
          <Button
            variant="normal"
            size="default"
            onClick={() => setIsEditDialogOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="default"
            onClick={() => handleEditSave()}
            loading={isUpdating}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditSession;
